// src/features/actionAreasSlice.js

import { createSlice, createAsyncThunk, createEntityAdapter } from '@reduxjs/toolkit';
import axiosInstance from '../axiosInstance';

import { logout } from './authSlice'; // Import the global logout action
import { selectUnitId } from './unitsSlice';

// Create an entity adapter
const actionAreasAdapter = createEntityAdapter();

// Initial state using the adapter's getInitialState
const initialState = actionAreasAdapter.getInitialState({
    unitId: null,
    status: 'idle',
    error: null,
    saveStatus: 'idle',
    saveError: null,
    createStatus: 'idle',
    createError: null,
    deleteStatus: {}, // To track delete status per actionArea
    deleteError: {}, // To track delete error per actionArea
    statusByUnit: {},
    errorByUnit: {},
});

// Async thunk to create a new action area
export const createActionArea = createAsyncThunk(
    'actionAreas/createActionArea',
    async (unitOverride, { getState, rejectWithValue }) => {
        try {
            const state = getState();
            const unitId = unitOverride || selectUnitId(state);

            if (!unitId) {
                throw new Error('Unit ID is not available in the state.');
            }

            const newActionArea = {
                measures: [],
                actionarea_title: "",
                unit: unitId,
                tag: null, // Initialize tag as null or a default value if necessary
            };

            const response = await axiosInstance.post(
                `/api/units/${unitId}/actionareas/`,
                newActionArea
            );

            return response.data; // Return API data directly
        } catch (error) {
            const errorMessage = error.response?.data?.detail || error.message || 'An unknown error occurred.';
            return rejectWithValue(errorMessage);
        }
    }
);

// Async thunk to fetch action areas with request deduplication
export const fetchActionAreas = createAsyncThunk(
    'actionAreas/fetchActionAreas',
    async (unitId, { rejectWithValue }) => {
        try {
            if (!unitId) {
                throw new Error('Unit ID is not available.');
            }

            const actionAreasResponse = await axiosInstance.get(`/api/units/${unitId}/actionareas/`);
            const actionAreasData = actionAreasResponse.data;

            return { unitId, actionAreas: actionAreasData };
        } catch (error) {
            const errorMessage = error.response?.data?.detail || error.message || 'An unknown error occurred.';
            return rejectWithValue({ unitId, error: errorMessage });
        }
    },
    {
        // Prevent duplicate requests while one is in-flight or for the same unit
        condition: (unitId, { getState }) => {
            const { actionAreas } = getState();
            const statusForUnit = actionAreas.statusByUnit[unitId];

            // If currently loading, don't start another request
            if (statusForUnit === 'loading') {
                return false;
            }

            // If we already have data for this unit, don't refetch
            if (statusForUnit === 'succeeded') {
                return false;
            }

            return true;
        },
    }
);

// Async thunk to save/update an action area
export const saveActionArea = createAsyncThunk(
    'actionAreas/saveActionArea',
    async (actionAreaToSave, { getState, rejectWithValue }) => {
        try {
            // Use the unit ID from the actionArea object being saved.
            // This makes the thunk more robust when called from contexts
            // where actionAreasSlice.unitId might not be the relevant one.
            const unitIdForApi = actionAreaToSave.unit;

            if (!unitIdForApi) {
                const errorMessage = 'Unit ID is missing in the actionArea object being saved.';
                console.error(errorMessage, actionAreaToSave);
                return rejectWithValue(errorMessage);
            }

            // Transform tags array into required string format
            const tagIds = actionAreaToSave.tags ? actionAreaToSave.tags.map(tag => tag.id) : [];
            const tagsString = JSON.stringify(tagIds);

            // Prepare API data, ensuring the unit field uses unitIdForApi
            const apiData = {
                ...actionAreaToSave,
                unit: unitIdForApi, // Crucially, use the unitId from the passed actionArea
                tags: tagsString,
                ends_at: actionAreaToSave.ends_at === '' ? null : actionAreaToSave.ends_at,
            };

            let response;
            // If actionAreaToSave.id exists and it's not marked as a new client-side entity,
            // then it's an update (PUT).
            if (actionAreaToSave.id && !actionAreaToSave.isNew) {
                response = await axiosInstance.put(`/api/units/${unitIdForApi}/actionareas/${actionAreaToSave.id}/`, apiData);
            } else {
                // Otherwise, it's a new entity (POST).
                // If actionAreaToSave.isNew is true, it might have a temporary client-side ID
                // that the backend might ignore or handle.
                // If actionAreaToSave.id is undefined/null, it's definitely a new entity.
                response = await axiosInstance.post(`/api/units/${unitIdForApi}/actionareas/`, apiData);
            }

            return response.data; // Return API data directly
        } catch (error) {
            const errorMessage = error.response?.data?.detail || error.message || 'An unknown error occurred.';
            console.error("Error in saveActionArea thunk:", errorMessage, "Action Area Data:", actionAreaToSave, "API Data:", error.config?.data);
            return rejectWithValue(errorMessage);
        }
    }
);

export const deleteActionArea = createAsyncThunk(
    'actionAreas/deleteActionArea',
    async (actionAreaId, { getState, rejectWithValue }) => {
        try {
            const state = getState();
            const actionArea = state.actionAreas.entities[actionAreaId];
            const unitId = actionArea?.unit || selectUnitId(state);

            if (!unitId) {
                throw new Error('Unit ID is not available in the state.');
            }

            await axiosInstance.delete(`/api/units/${unitId}/actionareas/${actionAreaId}/`);

            return actionAreaId; // Return the id to remove it from the store
        } catch (error) {
            const errorMessage = error.response?.data?.detail || error.message || 'An unknown error occurred.';
            return rejectWithValue({ actionAreaId, errorMessage });
        }
    }
);

// Slice definition
const actionAreasSlice = createSlice({
    name: 'actionAreas',
    initialState,
    reducers: {
        updateActionArea: actionAreasAdapter.updateOne,
        removeActionArea: actionAreasAdapter.removeOne,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchActionAreas.pending, (state, action) => {
                const unitId = action.meta.arg;
                state.status = 'loading';
                state.error = null;
                state.statusByUnit[unitId] = 'loading';
                state.errorByUnit[unitId] = null;
            })
            .addCase(fetchActionAreas.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const { unitId, actionAreas } = action.payload;
                state.unitId = unitId;
                state.statusByUnit[unitId] = 'succeeded';
                state.errorByUnit[unitId] = null;
                actionAreasAdapter.upsertMany(state, actionAreas);
            })
            .addCase(fetchActionAreas.rejected, (state, action) => {
                const { unitId, error } = action.payload || {};
                state.status = 'failed';
                if (unitId) {
                    state.statusByUnit[unitId] = 'failed';
                    state.errorByUnit[unitId] = error || action.error.message;
                } else {
                    state.error = error || action.error.message;
                }
            })
            .addCase(saveActionArea.pending, (state, action) => {
                state.saveStatus = 'saving';
                state.saveError = null;
                actionAreasAdapter.upsertOne(state, action.meta.arg);
            })
            .addCase(saveActionArea.fulfilled, (state, action) => {
                state.saveStatus = 'succeeded';
                const savedActionArea = action.payload; // Data from backend response
                const originalActionArea = action.meta.arg; // Data originally passed to thunk

                // If the original action area was marked as new (client-side flag)
                // and had a temporary ID that is different from the ID returned by the backend.
                if (originalActionArea.isNew && originalActionArea.id && originalActionArea.id !== savedActionArea.id) {
                    actionAreasAdapter.removeOne(state, originalActionArea.id); // Remove entity with temporary ID
                }
                
                // Upsert the action area using the data from the backend response.
                // This will add it if it's new (with the permanent ID from backend) or update it if it existed.
                actionAreasAdapter.upsertOne(state, savedActionArea);
            })
            .addCase(saveActionArea.rejected, (state, action) => {
                state.saveStatus = 'failed';
                state.saveError = action.payload;
            })
            .addCase(createActionArea.pending, (state) => {
                state.createStatus = 'loading';
                state.createError = null;
            })
            .addCase(createActionArea.fulfilled, (state, action) => {
                state.createStatus = 'succeeded';
                actionAreasAdapter.addOne(state, action.payload);
            })
            .addCase(createActionArea.rejected, (state, action) => {
                state.createStatus = 'failed';
                state.createError = action.payload;
            })
            .addCase(deleteActionArea.pending, (state, action) => {
                const actionAreaId = action.meta.arg;
                state.deleteStatus[actionAreaId] = 'loading';
                state.deleteError[actionAreaId] = null;
            })
            .addCase(deleteActionArea.fulfilled, (state, action) => {
                const actionAreaId = action.payload;
                state.deleteStatus[actionAreaId] = 'succeeded';
                actionAreasAdapter.removeOne(state, actionAreaId);
            })
            .addCase(deleteActionArea.rejected, (state, action) => {
                const { actionAreaId, errorMessage } = action.payload;
                state.deleteStatus[actionAreaId] = 'failed';
                state.deleteError[actionAreaId] = errorMessage;
            })
            .addCase(logout, () => initialState);
    },
});

// Export actions and selectors
export const { updateActionArea, removeActionArea } = actionAreasSlice.actions;
export const actionAreasSelectors = actionAreasAdapter.getSelectors((state) => state.actionAreas);
export default actionAreasSlice.reducer;
